import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  ["effective_date", "Effective date", false],
  ["client_name", "Client full name", true],
  ["client_address", "Client full address", false],
  ["authorized_person", "Authorized person for care decisions (if any)", false],
  ["authorized_type", "Authorized person type (POA / legal representative)", false],
  ["emergency_phone", "Emergency contact phone", false],
  ["email", "Email", false],
  ["start_date", "Preferred start date", false],
  ["shift1", "Shift 1 (days / time / caregiver)", false],
  ["shift2", "Shift 2 (days / time / caregiver)", false],
  ["shift3", "Shift 3 (days / time / caregiver)", false],
  ["service_notes", "Services requested / notes", false],
  ["rate", "Service rate (private pay)", false],
  ["rate_per", "Rate per (visit / hour / week)", false],
];

const SERVICE_TYPES = [
  ["pas_chc", "Personal Assistance Services (Community HealthChoices)"],
  ["veterans", "Home Care Assistance (Veteran's)"],
  ["personal_care", "Personal Care (Private)"],
  ["ltc", "Long-Term Care (Insurance)"],
  ["companionship", "Companionship (Private)"],
  ["housekeeping", "Housekeeping (Private)"],
  ["transportation", "Transportation (Private)"],
];

export default function AgreementFormPage() {
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const generate = async () => {
    if (!(data.client_name || "").trim()) { toast.error("Client name is all we need to start"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/public/forms/agreement_receive_services/fill`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      a.download = `agreement-${(data.client_name || "client").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Agreement downloaded — print it and sign");
    } catch { toast.error("Something went wrong — call the office at 800-719-6912"); }
    setBusy(false);
  };

  const inputCls = "w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-base text-[#23282f] placeholder:text-stone-400 focus:border-[#6A3E97] focus:outline-none focus:ring-2 focus:ring-[#6A3E97]/20 transition-colors";

  return (
    <div className="min-h-screen bg-[#f6f7f9] font-sans text-[#23282f]" data-testid="agreement-form-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/"><img src="/shc-logo.png" alt="Silver Home Care" className="h-12 w-auto" /></Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-[#6A3E97]" data-testid="agreement-back-home">
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="font-heading text-3xl font-medium tracking-tight sm:text-4xl" data-testid="agreement-title">Agreement to Receive Services</h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-stone-600">
          Fill in the client and service details below. We'll produce the completed agreement as a PDF —
          print it and sign where indicated.
        </p>

        <div className="mt-8 rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="space-y-4">
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-stone-700">Service types (mark all that apply)</legend>
              <div className="grid gap-2 sm:grid-cols-2" data-testid="agreement-service-types">
                {SERVICE_TYPES.map(([key, label]) => (
                  <label key={key} className="flex cursor-pointer items-start gap-2 text-sm text-stone-700" data-testid={`agreement-type-${key}`}>
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#6A3E97]"
                      checked={(data.service_types || []).includes(key)}
                      onChange={(e) => setData((d) => ({
                        ...d,
                        service_types: e.target.checked
                          ? [...(d.service_types || []), key]
                          : (d.service_types || []).filter((k) => k !== key),
                      }))} />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
            {FIELDS.map(([key, label, req]) => (
              <div key={key}>
                <label className="mb-1 block text-sm font-medium text-stone-700">{label}{req ? " *" : ""}</label>
                <input value={data[key] || ""} onChange={set(key)} className={inputCls} data-testid={`agreement-${key}`} />
              </div>
            ))}
          </div>
          <button onClick={generate} disabled={busy}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#6A3E97] px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A] disabled:opacity-50"
            data-testid="agreement-generate">
            <Printer size={18} /> {busy ? "Preparing…" : "Download completed agreement — print & sign"}
          </button>
          <p className="mt-3 text-xs text-stone-400">
            The completed PDF downloads — open it, print it, sign it. Prefer the blank form?{" "}
            <a href={`${API}/api/public/forms/agreement_receive_services`} target="_blank" rel="noreferrer" className="font-medium text-[#6A3E97] hover:underline" data-testid="agreement-blank-link">
              Download the blank agreement
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
